import { IAvailableCaptionsItem } from '../../spa/store/current-video/types';
import {
  ICaptionsItem,
  IYoutubeVideoInfo,
  IYoutubeVideoInfoFull,
  LANG_CODE_AUTO_GEN_SUBSTR
} from '../../spa/types/common';
import { APP_KEY } from '../constants';
import _ from 'lodash';
import { EN_AUTO_GEN_LANG_CODE } from '../../spa/store/models/types';
import { EExtMessageType, ExtMessageSender } from './ext-message-sender';
import { TVideoInfoResponse, VideoMetaLangsRest } from '../rest/videoMeta/getVideoMetaLangsRest';
import { UrlUtil } from './url-util';
import { YoutubeUrlUtil } from './youtube-url-util';
import { LangUtil } from './lang-util';
import { VideoLangRest } from '../rest/videoLang/videoLangRest';

export interface IVideoMetaInfo {
  loaded: boolean,
  captions?: IAvailableCaptionsItem[],
  videoInfo?: IYoutubeVideoInfo,
  videoId?: string
}

export class VideoMetaInfo {

  public static async getMetaInfoByVideoId(videoId: string): Promise<IVideoMetaInfo>  {
    const url = YoutubeUrlUtil.getUrlById(videoId);
    return VideoMetaInfo.getMetaInfoByVideoUrl(url);
  }

  public static async getMetaInfoByVideoUrl(videoUrl: string): Promise<IVideoMetaInfo> {
      let captions = await VideoMetaInfo.downloadLangFromServiceWorker(videoUrl);

      captions = VideoMetaInfo.checkAutogenCaptions(captions || []);

      let videoId;
      if (captions?.length) {
        const query = UrlUtil.getObjectFromUrl(captions[0].url);
        videoId = query.v;
      } else {
        videoId = YoutubeUrlUtil.getVideoId(videoUrl);
      }

      const videoInfo: IYoutubeVideoInfo = await VideoMetaInfo.getVideoInfo(videoId);
      const videoMetaInfo: IVideoMetaInfo = {loaded: !!captions?.length, captions, videoInfo, videoId};

      return videoMetaInfo;
  }

  public static async getMetaInfoWithCaptionsByVideoUrl(videoKey: string, groupId: number, groupLangCodes: string[]): Promise<TVideoInfoResponse> {
    const savedLangCodes: string[] = [];
    const videoInfo = await VideoMetaLangsRest.getVideoInfo(videoKey, groupId);

    let savedCaptionsLangCodes = Object.keys(videoInfo.captions) || [];
    savedCaptionsLangCodes = savedCaptionsLangCodes.filter(langCode => !!videoInfo.captions[langCode]?.length)

    let metaLangCodes = videoInfo.videoInfo.langs?.split(',').filter(l => l?.length > 0).map(l => LangUtil.checkLangCode(l))
    const metaLangCodesLoaded = metaLangCodes?.length > 0;

    let downloadedLangItems: IAvailableCaptionsItem[];
    if (!metaLangCodesLoaded) {
      //downloadedLangItems = await VideoMetaInfo.downloadLangFromServiceWorker(YoutubeUrlUtil.getUrlById(videoKey));
      downloadedLangItems = await this.downloadLangFromServiceWorkerByKey(videoKey);
      if (downloadedLangItems) {
        metaLangCodes = downloadedLangItems.map(item => LangUtil.checkLangCode(item.code));
        await VideoLangRest.saveVideoInfoLangs(videoKey, metaLangCodes);
      }
    }

    let notSavedGroupLangCodes = groupLangCodes.filter(groupLangCode => {
      return !savedCaptionsLangCodes.includes(groupLangCode);
    })

    notSavedGroupLangCodes = notSavedGroupLangCodes.filter(groupLangCode => {
      return metaLangCodes.includes(groupLangCode)
    })

    if (notSavedGroupLangCodes.length > 0 && !downloadedLangItems) {
      //downloadedLangItems = await VideoMetaInfo.downloadLangFromServiceWorker(YoutubeUrlUtil.getUrlById(videoKey));
      downloadedLangItems = await this.downloadLangFromServiceWorkerByKey(videoKey);
      if (downloadedLangItems) {
        metaLangCodes = downloadedLangItems.map(item => LangUtil.checkLangCode(item.code));
        await VideoLangRest.saveVideoInfoLangs(videoKey, metaLangCodes);
      }
    }

    if (downloadedLangItems) {
      for await(const langCode of notSavedGroupLangCodes) {
        const lang = this.findLangAvailableCaptionsItemByCode(downloadedLangItems, langCode);
        if (lang) {
          const videoCaptions = await this.getVideoCaptions(videoKey, lang.code);
          videoInfo.captions[langCode] = videoCaptions;

          const alternatesLangCodes = LangUtil.getAlternativeLangCodes(langCode);
          alternatesLangCodes.forEach(langCode => {
            if (!videoInfo.captions[langCode])
              videoInfo.captions[langCode] = videoCaptions;
          })

          const captionsExist = await VideoLangRest.checkExitsVideoCaptions(videoKey, langCode);
          if (!captionsExist) {
            await VideoLangRest.saveVideoCaptions(videoKey, langCode, videoCaptions);
            savedLangCodes.push(langCode);
          }
        }
      }
    }

    videoInfo.savedLangCodes = [...savedLangCodes];
    return videoInfo;
  }

  private static async downloadLangFromServiceWorkerByKey(videoKey: string): Promise<IAvailableCaptionsItem[] | null> {
    let result = await VideoMetaInfo.downloadLangFromServiceWorker(YoutubeUrlUtil.getUrlById(videoKey));
    if (result) return result;

    const ps = videoKey.lastIndexOf('-');
    if (ps >= 0) {
      const num = parseInt(videoKey.substring(ps+ 1));
      if (num !== undefined) {
        const key = videoKey.substring(0, ps);
        result = await VideoMetaInfo.downloadLangFromServiceWorker(YoutubeUrlUtil.getUrlById(key));
        return result;
      }
    }

    return null;
  }

  private static findLangAvailableCaptionsItemByCode(langs: IAvailableCaptionsItem[], code: string): IAvailableCaptionsItem | undefined {
    let lang = langs.find(l => l.code === code);
    if (lang) return lang;
    lang = langs.find(l => LangUtil.checkLangCode(l.code) === code);
    return lang;
  }

  /*public static getLangs(videoId: string, directDownload: boolean = false): TAsyncThunk {
    return async (
      dispatch,
      getState
    ): Promise<IAvailableCaptionsItem[]> => {
      const url = YoutubeUrlUtil.getUrlById(videoId);
      const captions = directDownload ?
        await dispatch(VideoMetaInfo.downloadLangsDirect(url)) :
        await dispatch(VideoMetaInfo.downloadLangs(url));
      return VideoMetaInfo.checkAutogenCaptions(captions);
    }
  }*/

  /*private static downloadLangsDirect(videoUrl: string): TAsyncThunk {
    return async (
      dispatch,
      getState
    ): Promise<IAvailableCaptionsItem[]> => {
      //return await dispatch(getVideoMetaLangsRest(videoId));
      return fetch(videoUrl)
        .then((response) => {
          return response.text();
        }).then(text => {
          return VideoMetaInfo.prepareLangItems(VideoMetaInfo.parseLangsFromResp(text));
        })
    }
  }*/

 /* private static downloadLangs(videoUrl: string): TAsyncThunk {
    return async (
      dispatch,
      getState
    ): Promise<IAvailableCaptionsItem[]> => {
      /!*if (isExtensionInstalled(getState())) {
        return dispatch(VideoMetaInfo.downloadLangFromServiceWorker(videoId));
      }*!/
      return VideoMetaInfo.downloadLangFromBackend(videoUrl);
    }
  }*/

  public static async downloadLangFromServiceWorker(videoUrl: string): Promise<IAvailableCaptionsItem[] | null> {
      const text = await ExtMessageSender.send({
        type: EExtMessageType.GET_VIDEO_CAPTIONS,
        payload: {videoUrl}
      });
      if (!text) return null;
      return VideoMetaInfo.prepareLangItems(VideoMetaInfo.parseLangsFromResp(text));
  }

  /*private static async downloadLangFromBackend(videoUrl: string): Promise<IAvailableCaptionsItem[] | null> {
    const result: TVideoMetaLangsResponse = await VideoMetaLangsRest.getVideoMeta(videoUrl);
    if (result.success) {
      return VideoMetaInfo.prepareLangItems(result.langs);
    }
    return null;
  }*/

  private static parseLangsFromResp(text: string): IAvailableCaptionsItem[] {
    const startSubstr = '"captionTracks":[';
    const startPos = text.indexOf(startSubstr);
    if (startPos < 0) return [];
    text = text.substring(startPos + startSubstr.length-1);
    const endPos = text.indexOf(']');
    if (endPos < 0) return [];
    text = text.substring(0, endPos+1);
    const result: IAvailableCaptionsItem[] = JSON.parse(text).map((item: any) => {
      return {
        name: item.name.simpleText,
        code: item.languageCode,
        url: item.baseUrl
      }
    });
    return result;
  }


  private static prepareLangItems(items: IAvailableCaptionsItem[]): IAvailableCaptionsItem[] {
    return items.map((item: IAvailableCaptionsItem) => {
      if (item.code === 'en' && item.name.toLowerCase().indexOf('(auto-generated)') >= 0) {
        const result = {...item};
        result.code = EN_AUTO_GEN_LANG_CODE;
        return result;
      }
      return item;
    });
  }

  public static async getVideoInfo(videoId: string): Promise<IYoutubeVideoInfo> {
    const url = 'https://www.googleapis.com/youtube/v3/videos?id='+videoId+'&key='+APP_KEY+'&fields=items(snippet(title,description,thumbnails,channelTitle,defaultAudioLanguage))&part=snippet';
    const response = await fetch(url);
    const data = await response.json();
    return VideoMetaInfo.parseVideoInfo(data);
  }

  public static getVideoInfoFull(videoId: string): Promise<IYoutubeVideoInfoFull> {
    const url = 'https://www.googleapis.com/youtube/v3/videos?id='+videoId+'&key='+APP_KEY+'&part=snippet';
    return fetch(url)
      .then((response) => {
        return response.json();
      })
      .then((data) => {
        return _.get(data, 'items[0].snippet');
      });
  }

  private static parseVideoInfo(json: any): IYoutubeVideoInfo {
    return {
      title: _.get(json, 'items[0].snippet.title'),
      description: _.get(json, 'items[0].snippet.description'),
      thumbnailUrl: _.get(json, 'items[0].snippet.thumbnails.default.url'),
      thumbnailMediumUrl: _.get(json, 'items[0].snippet.thumbnails.medium.url'),
      thumbnailStandardUrl: _.get(json, 'items[0].snippet.thumbnails.standard.url'),
      channelTitle: _.get(json, 'items[0].snippet.channelTitle'),
      defaultAudioLanguage: _.get(json, 'items[0].snippet.defaultAudioLanguage'),
    }
  }

  private static async getVideoCaptions(videoId: string, langCode: string): Promise<ICaptionsItem[]> {

    return VideoMetaLangsRest.getVideoCaptions(videoId, langCode);
  }



 /* private static getVideoCaptions(availableCaptionsItem: IAvailableCaptionsItem): Promise<ICaptionsItem[]> {
    return new Promise((resolve, reject) => {
      let xhr = new XMLHttpRequest();
      xhr.open('GET', availableCaptionsItem.url, true);
      xhr.responseType = 'document';
      xhr.onload = function() {
        if (this.status >= 200 && this.status < 400) {
          //const checkOverlay = availableCaptionsItem.code.indexOf(LANG_CODE_AUTO_GEN_SUBSTR) > 0;
          resolve(VideoMetaInfo.parseVideoCaptions(this.response, availableCaptionsItem.isAsr));
        } else {
          reject('Error: ' + this.status);
        }
      };
      xhr.onerror = function(error) {
        reject(error);
      };
      xhr.send();
    });
  };*/

  private static parseVideoCaptions(xml: Document, isAsr: boolean): Promise<ICaptionsItem[]> {
    return new Promise((resolve, reject) => {
      let captions: ICaptionsItem[] = [];
      const nodes = [...xml.querySelectorAll('transcript > text')];
      if (!isAsr) {
        captions = nodes.map((node) => {
          const startTime = parseFloat(node.getAttribute('start') as string);
          const duration = parseFloat(node.getAttribute('dur') as string);
          const endTime = startTime + duration;
          const text = (node.textContent as string).replace(/&#(\d+);/g, (_, charCode) =>
            String.fromCharCode(charCode)
          ); // Decode HTML entities (like &#39;)
          return { duration, startTime, endTime, text };
        });
      } else {
        // YouTube ASR (auto-generated) Captions tend to overlap the "start" and "dur"
        // Let's combine each two of them into one line, just like what YouTube does for translation
        for (let i = 0; i < nodes.length; i += 2) {
          const startTime = parseFloat(nodes[i].getAttribute('start') as string);
          const duration = parseFloat(nodes[i].getAttribute('dur') as string);
          const endTime = startTime + duration;
          let text = nodes[i].textContent + (nodes[i + 1] ? ' ' + nodes[i + 1].textContent : '');
          text = text.replace(/&#(\d+);/g, (_, charCode) => String.fromCharCode(charCode));
          captions.push({ duration, startTime, endTime, text });
        }
      }
      VideoMetaInfo.checkCaptions(captions);
      resolve(captions);
    });
  }

  public static checkCaptions(captions: ICaptionsItem[]) {
    VideoMetaInfo.checkOverlayCaptions(captions);
    VideoMetaInfo.checkDurationCaptions(captions);
    VideoMetaInfo.checkTextCaptions(captions);
    VideoMetaInfo.checkCaptionsTranstator1Line(captions);
  }

  private static checkOverlayCaptions(captions: ICaptionsItem[]) {
    for(let i=0; i<captions.length-1; i++) {
      if (captions[i].endTime > captions[i+1].startTime || captions[i].endTime < captions[i+1].startTime) {
        captions[i].endTime = captions[i+1].startTime;
      }
    }
  }

  private static checkDurationCaptions(captions: ICaptionsItem[]) {
    for(let i=0; i<captions.length-1; i++) {
      if (!captions[i].duration) {
        captions[i].duration = captions[i].endTime - captions[i].startTime;
      }
    }
  }

  private static checkTextCaptions(captions: ICaptionsItem[]) {
    const spacesRegex = / +/gi;
    const newLineRegex = /[\n\r]/gi;
    for(let i=0; i<captions.length-1; i++) {
      captions[i].text = _.unescape(captions[i].text);
      captions[i].text = captions[i].text.replaceAll(newLineRegex, ' ');
      captions[i].text = captions[i].text.replaceAll(spacesRegex, ' ');
    }
  }

  private static checkAutogenCaptions(list: IAvailableCaptionsItem[]) {
    const result = [...list];
    result.forEach(caption => {
      if (caption.code.indexOf(LANG_CODE_AUTO_GEN_SUBSTR) < 0 &&
        caption.name.toLocaleLowerCase().indexOf('(auto-generated)') > 0) {
        caption.code += LANG_CODE_AUTO_GEN_SUBSTR;
      }
    })
    return result;
  }

  private static checkCaptionsTranstator1Line(captions: ICaptionsItem[]) {
    if (captions.length > 0 && captions[0].text.indexOf('Переводчик:') === 0) {
      captions.splice(0, 1);
      captions[0].startTime = 0;
      captions[0].duration = captions[0].endTime - captions[0].startTime;
    }
  }

  public static getVideoPriorityTargetLang(
    videoLangCodes: string[],
    userTargetLang: string,
    videoInfo?: IYoutubeVideoInfo
  ): string {
		const audioCode = LangUtil.checkLangCode(videoInfo?.defaultAudioLanguage || '');
		if (audioCode && videoLangCodes.includes(audioCode)) {
			return audioCode;
		}
		if (videoLangCodes.includes(userTargetLang))
			return userTargetLang;
		return videoLangCodes.length ? videoLangCodes[0] : '';
	}

}
